<template>
  <div v-if="show" class="password-section">
    <div class="password-card">
      <div class="password-header">
        <i class="las la-lock"></i>
        <h3>Password Required</h3>
        <p>{{ description || 'This file is password protected. Please enter the password to proceed.' }}</p>
      </div>
      <div class="password-form">
        <div class="form-group">
          <label :for="inputId">Password</label>
          <input
            :id="inputId"
            v-model="password"
            type="password"
            class="password-input"
            placeholder="Enter password"
            @keyup.enter="handleSubmit"
            :disabled="submitting"
            ref="passwordInput"
          />
        </div>
        <button 
          @click="handleSubmit" 
          class="submit-btn"
          :disabled="!password.trim() || submitting"
        >
          <BaseSpinner v-if="submitting" size="small" />
          <i v-else class="las la-unlock"></i>
          {{ submitting ? 'Verifying...' : 'Submit Password' }}
        </button>
      </div>
      <div v-if="error" class="password-error">
        <i class="las la-exclamation-triangle"></i>
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import BaseSpinner from '@/components/UI/BaseSpinner.vue'

export default {
  name: 'PasswordInput',
  components: {
    BaseSpinner
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    description: {
      type: String,
      default: null
    },
    submitting: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    inputId: {
      type: String,
      default: 'password-input'
    }
  },
  data() {
    return {
      password: ''
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.passwordInput) {
            this.$refs.passwordInput.focus()
          }
        })
      } else {
        this.password = ''
      }
    }
  },
  methods: {
    handleSubmit() {
      if (!this.password.trim()) {
        return
      }
      this.$emit('submit', this.password)
    },
    clearPassword() {
      this.password = ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.password-section {
  width: 100%;
  margin-top: 1rem;
  
  .password-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    .password-header {
      text-align: center;
      margin-bottom: 1.5rem;
      
      i {
        font-size: 2rem;
        color: #f59e0b;
        margin-bottom: 0.5rem;
        display: block;
      }
      
      h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0 0 0.5rem 0;
        font-family: $brand-font-medium;
      }
      
      p {
        color: #6b7280;
        margin: 0;
        font-size: 0.9rem;
        font-family: $brand-font-regular;
      }
    }
    
    .password-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      
      .form-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        
        label {
          font-size: 0.9rem;
          font-weight: 500;
          color: #374151;
          font-family: $brand-font-medium;
        }
        
        .password-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.9rem;
          font-family: $brand-font-regular;
          color: #2d3748;
          transition: all 0.2s ease;
          
          &:focus {
            outline: none;
            border-color: $color-primary;
            box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
          }
          
          &:disabled {
            background: #f8f9fa;
            cursor: not-allowed;
            opacity: 0.6;
          }
          
          &::placeholder {
            color: #9ca3af;
          }
        }
      }
      
      .submit-btn {
        background: $color-primary;
        color: white;
        border: none;
        padding: 0.75rem 1.5rem;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: all 0.2s ease;
        font-family: $brand-font-medium;
        
        &:hover:not(:disabled) {
          background: darken($color-primary, 10%);
          transform: translateY(-1px);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        i {
          font-size: 1rem;
        }
      }
    }
    
    .password-error {
      margin-top: 1rem;
      padding: 0.75rem;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 6px;
      color: #dc2626;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-family: $brand-font-regular;
      
      i {
        font-size: 1rem;
        flex-shrink: 0;
      }
    }
  }
}

@media (max-width: 768px) {
  .password-section {
    .password-card {
      padding: 1rem;
      
      .password-header {
        h3 {
          font-size: 1.1rem;
        }
      }
      
      .password-form {
        .submit-btn {
          padding: 0.75rem 1rem;
          font-size: 0.85rem;
        }
      }
    }
  }
}
</style>

